import React from "react";
import {
  Rocket, Smartphone, Facebook, Link2, DollarSign, Music2, ShieldAlert, Award,
  CheckCircle2, ExternalLink, MessageCircle, Building2, Sparkles, Megaphone, AlertTriangle,
} from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../../components/ui/accordion";

const LinkBtn = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer"
    className="inline-flex items-center gap-1.5 mt-2 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-lg px-3.5 py-2 text-xs transition-colors">
    {children} <ExternalLink size={13} />
  </a>
);

const Steps = ({ items }) => (
  <ol className="space-y-3 mt-1">
    {items.map((it, i) => (
      <li key={typeof it === "string" ? it : `paso-${items.length}-${i}`} className="flex gap-3">
        <span className="shrink-0 w-7 h-7 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-bold">{i + 1}</span>
        <div className="pt-0.5 text-sm text-zinc-300 leading-relaxed">{it}</div>
      </li>
    ))}
  </ol>
);

const BOX_TONES = {
  purple: "border-purple-500/30 bg-purple-500/5",
  yellow: "border-yellow-400/30 bg-yellow-400/5",
  green: "border-green-500/30 bg-green-500/5",
  red: "border-red-500/30 bg-red-500/5",
  blue: "border-blue-500/30 bg-blue-500/5",
};

const Box = ({ tone = "purple", icon: Icon, title, children }) => (
  <div className={`rounded-xl border ${BOX_TONES[tone]} p-4 mt-3`}>
    {title && <div className="flex items-center gap-2 mb-2 font-semibold text-zinc-100">{Icon && <Icon size={16} className="text-zinc-300" />} {title}</div>}
    <div className="text-sm text-zinc-300 leading-relaxed space-y-2">{children}</div>
  </div>
);

const Section = ({ value, icon: Icon, title, color = "text-purple-400", banner, children }) => (
  <AccordionItem value={value} data-testid={`section-${value}`} className="border border-white/10 rounded-2xl bg-[#121212] px-5 mb-3 overflow-hidden">
    <AccordionTrigger className="hover:no-underline py-5">
      <span className="flex items-center gap-3 text-left font-display font-bold text-base sm:text-lg">
        <span className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center shrink-0"><Icon size={18} className={color} /></span>
        {title}
      </span>
    </AccordionTrigger>
    <AccordionContent className="pb-6 text-zinc-300">
      {banner && <img src={`/antes/${banner}.png`} alt="" loading="lazy" className="w-full h-40 sm:h-48 object-cover rounded-xl mb-4 border border-white/10" />}
      {children}
    </AccordionContent>
  </AccordionItem>
);

export default function BeforeYouStart() {
  return (
    <div data-testid="before-start-page" className="max-w-3xl">
      <div className="flex items-center gap-3 mb-2">
        <Rocket className="text-yellow-impact" size={26} />
        <h1 className="font-display font-black tracking-tight text-3xl">Antes de empezar</h1>
      </div>
      <p className="text-zinc-400 mb-8">Tu guía completa para publicitar tu campaña en Facebook, Instagram y TikTok de forma <strong className="text-zinc-200">efectiva y sin que te rechacen los anuncios</strong>. Ábre cada sección y sigue los pasos.</p>

      <div className="rounded-2xl border border-yellow-400/30 bg-yellow-400/5 p-5 mb-6 flex gap-3">
        <Sparkles className="text-yellow-impact shrink-0 mt-0.5" size={20} />
        <p className="text-sm text-zinc-200">Una vez que ya generaste y aprobaste el <strong>mensaje, las imágenes o el video</strong> de tu campaña en MULTICONTACTOS, así es como puedes promocionarla. Tienes <strong>dos formas</strong> de crear anuncios: desde tus redes personales (lo más fácil) o como una agencia con el administrador de anuncios (avanzado).</p>
      </div>

      <Accordion type="single" collapsible defaultValue="meta-facil" className="w-full">

        <Section value="meta-facil" icon={Megaphone} title="Opción 1: Promociona desde tus redes (lo más fácil)" color="text-blue-400" banner="social-personal">
          <p>El botón <strong>"Promocionar publicación"</strong> aparece directamente debajo de tus fotos o videos en Instagram y Facebook. Solo eliges cuánto dinero gastar, a qué tipo de personas quieres llegar (edad, ciudad, intereses) y listo. Ideal para empezar.</p>

          <Box tone="purple" icon={Smartphone} title="Prepara tus herramientas (antes de pagar)">
            <Steps items={[
              <span><strong>Instala WhatsApp Business:</strong> descarga esta app gratuita en tu celular. Es igual al WhatsApp normal, pero te permite conectarlo con Facebook. Usa el mismo número que ya tienes.</span>,
              <span><strong>Crea tu Página de Facebook:</strong> desde tu Facebook personal entra al menú (las tres rayitas o tu foto), busca <em>Páginas</em> y toca <em>Crear</em>. Ponle el nombre de tu negocio o el tuyo (ej. "Bienestar y Salud con [Tu Nombre]").</span>,
            ]} />
          </Box>

          <Box tone="purple" icon={Link2} title="Paso 2: Vincula tu WhatsApp con Facebook">
            <Steps items={[
              "Abre tu nueva Página de Facebook.",
              "Busca el botón de Configuración (un icono de engranaje).",
              "Toca en Cuentas vinculadas y luego elige WhatsApp.",
              "Escribe tu número; Meta te enviará un código por mensaje. Escríbelo en la pantalla para enlazar las cuentas.",
            ]} />
          </Box>

          <Box tone="purple" icon={Megaphone} title="Paso 3: Crea el anuncio paso a paso">
            <p className="mb-2">Lo ideal es hacerlo desde una computadora en la herramienta oficial gratuita <strong>Meta Business Suite</strong>. Si usas el celular, descarga la app "Anuncios de Meta".</p>
            <Steps items={[
              <span><strong>Inicia la creación:</strong> toca el botón azul <em>Crear anuncio</em>.</span>,
              <span><strong>Elige tu objetivo:</strong> selecciona <em>Recibir más mensajes</em> (activa el botón "Enviar mensaje de WhatsApp" en tu publicidad).</span>,
              <span><strong>Escribe el texto:</strong> sube una foto bonita de tu producto o de ti. Ejemplo permitido: <em>"¡Hola! Te ayudo a mejorar tu energía diaria con suplementos naturales. Haz clic en el botón y platicamos por WhatsApp para una asesoría personalizada."</em></span>,
              <span><strong>Agrega el enlace de tu página:</strong> pega ahí la dirección de tu Página de Aterrizaje para que también puedan visitarla.</span>,
              <span><strong>Elige a tu público:</strong> <em>Lugares</em> (tu ciudad o todo el país si envías a domicilio), <em>Edad</em> (ej. 30 a 60 años) e <em>Intereses</em> ("Nutrición", "Cuidado personal", "Estilo de vida saludable").</span>,
              <span><strong>Define tu presupuesto:</strong> elige cuánto gastar al día. Puedes empezar muy bajo (desde $30–$50 MXN al día) para probar.</span>,
              <span><strong>Paga el anuncio:</strong> ingresa tu tarjeta de débito, crédito o PayPal de forma segura. Meta solo te cobra lo que decidiste gastar.</span>,
              <span><strong>Publica:</strong> revisa todo y toca <em>Promocionar</em>. Meta tardará unas horas en revisarlo y empezará a circular.</span>,
            ]} />
            <LinkBtn href="https://business.facebook.com/">Abrir Meta Business Suite</LinkBtn>
          </Box>
        </Section>

        <Section value="meta-agencia" icon={Building2} title="Opción 2: Administrador de Anuncios (avanzado, como agencia)" color="text-purple-400" banner="ads-manager">
          <p><strong>Meta Business Suite / Administrador de Anuncios</strong> es una plataforma web gratuita de Meta. No requiere código, solo sigues pasos visuales. Te da mucho más control para segmentar a tu público, elegir dónde se muestra el anuncio y medir tus resultados de forma profesional. Ahí también encuentras los medios de pago.</p>
          <LinkBtn href="https://business.facebook.com/">Abrir Administrador de Anuncios</LinkBtn>
        </Section>

        <Section value="costo" icon={DollarSign} title="¿Cuánto cuesta anunciar?" color="text-green-400" banner="budget">
          <Box tone="green" icon={DollarSign}>
            <p>El costo diario en estas redes va desde <strong>$20 MXN (≈ $1 USD)</strong> al día. Te recomendamos campañas de <strong>mínimo $50 MXN (≈ $3 USD)</strong> al día para que tu campaña tenga mayor difusión y mejores resultados.</p>
            <LinkBtn href="https://www.facebook.com/business/ads">Conocer más sobre anuncios de Meta</LinkBtn>
          </Box>
        </Section>

        <Section value="tiktok" icon={Music2} title="Cómo anunciar en TikTok" color="text-zinc-100" banner="tiktok">
          <Box tone="blue" icon={Music2} title="Paso 1: Crea tu cuenta de anuncios">
            <Steps items={[
              <span>Entra desde tu computadora a <strong>ads.tiktok.com</strong> y haz clic en <em>Comenzar ahora</em>.</span>,
              "Regístrate con tu correo y crea una contraseña.",
              "Completa los datos de tu negocio (nombre, país, moneda en pesos mexicanos y sector). Elige cuenta Individual o De Negocios según prefieras.",
            ]} />
            <LinkBtn href="https://ads.tiktok.com/">Abrir TikTok Ads</LinkBtn>
          </Box>

          <Box tone="blue" icon={Megaphone} title="Paso 2: Crea la campaña y elige el objetivo">
            <Steps items={[
              <span><strong>Selecciona el Modo personalizado</strong> (Custom Mode) para tener todas las opciones.</span>,
              <span><strong>Objetivo publicitario:</strong> elige <em>Tráfico</em> (envía a las personas fuera de TikTok hacia tu sitio web o tu enlace de WhatsApp).</span>,
              <span><strong>Nombre de la campaña:</strong> ej. "Invitación al Negocio - WhatsApp". Deja las demás opciones como están y continúa.</span>,
            ]} />
          </Box>

          <Box tone="blue" icon={ShieldAlert} title="Paso 3: Define el público y el presupuesto">
            <Steps items={[
              <span><strong>Emplazamiento:</strong> deja seleccionada solo la casilla de TikTok.</span>,
              <span><strong>Ubicación:</strong> selecciona México (o tus estados/ciudades).</span>,
              <span><strong>Demografía:</strong> Género: Todos. Edad: rangos maduros con capacidad de inversión (25-34, 35-54, 55+). Evita menores de edad.</span>,
              <span><strong>Intereses:</strong> "Emprendimiento", "Negocios locales", "Desarrollo personal", "Cuidado personal".</span>,
              <span><strong>Presupuesto diario:</strong> a diferencia de Meta, TikTok exige un <strong>mínimo de $200 MXN diarios</strong> por grupo de anuncios. Puedes programarlo solo 2 o 3 días para una prueba controlada.</span>,
            ]} />
            <LinkBtn href="https://ads.tiktok.com/business/es/guides/promote-small-business-tiktok">Guía oficial de TikTok</LinkBtn>
          </Box>

          <Box tone="blue" icon={MessageCircle} title="Paso 4: Sube tu video y pon el enlace de WhatsApp">
            <Steps items={[
              "Sube el video vertical que grabaste con tu celular.",
              <span><strong>Texto del anuncio:</strong> frase corta (máx. 100 caracteres). Ej: "Aprende a emprender en equipo y con horarios flexibles. Toca el botón y platiquemos."</span>,
              <span><strong>Botón (llamado a la acción):</strong> elige <em>Más información</em> o <em>Contáctanos</em>.</span>,
              <span><strong>Destino (URL):</strong> pega el enlace a tu sitio web o a tu chat de WhatsApp.</span>,
            ]} />
            <Box tone="yellow" icon={Link2} title="Tip: crea un enlace corto de WhatsApp">
              <p>Usa la herramienta gratuita <strong>Walink</strong> para generar un enlace corto. Así, al tocar el botón del anuncio, se abre tu WhatsApp con un mensaje automático tipo: "Hola, quiero más información del proyecto de emprendimiento".</p>
              <LinkBtn href="https://crear.wa.link/">Crear mi link de WhatsApp</LinkBtn>
            </Box>
          </Box>

          <Box tone="blue" icon={DollarSign} title="Paso 5: Configura el pago y lanza">
            <Steps items={[
              "Haz clic en Enviar.",
              "Configura tu método de pago (tarjeta de débito/crédito o PayPal).",
              "Tu anuncio pasará a revisión de TikTok (entre 2 y 24 horas). En cuanto lo aprueben, empezará a mostrarse.",
            ]} />
            <p className="text-xs text-zinc-400 mt-2">💡 Los videos grabados por ti de forma natural funcionan mucho mejor que las imágenes fijas con texto.</p>
          </Box>
        </Section>

        <Section value="consejos" icon={AlertTriangle} title="Consejos para que NO rechacen tus anuncios" color="text-yellow-impact" banner="policies">
          <Box tone="red" icon={ShieldAlert}>
            <p>La clave absoluta: <strong>nunca prometas ingresos, lujos</strong> ni uses palabras de multinivel, vacantes de empleo o "dinero rápido". TikTok prohíbe estrictamente los modelos MLM y Meta penaliza los ganchos financieros exagerados.</p>
            <p>Enfoca tus mensajes en el <strong>desarrollo personal, el aprendizaje de nuevas habilidades y el emprendimiento en equipo</strong>.</p>
            <div className="flex flex-wrap gap-2 mt-1">
              <LinkBtn href="https://www.facebook.com/business/help/488043719226449">Políticas de Meta</LinkBtn>
              <LinkBtn href="https://ads.tiktok.com/help/article/tiktok-ads-policy-financial-services">Políticas de TikTok</LinkBtn>
            </div>
          </Box>
        </Section>

        <Section value="reglas" icon={Award} title="Tres reglas de oro para que aprueben tus anuncios" color="text-yellow-impact">
          <Steps items={[
            <span><strong>En imágenes o videos:</strong> jamás muestres fajos de billetes, autos de lujo, casas enormes ni cheques de comisiones. Muestra fotos tuyas, equipos sonriendo, cuadernos de notas o los productos físicos.</span>,
            <span><strong>En tu página de destino:</strong> tampoco debe hablar de "hacerse rico", "libertad financiera inmediata" ni usar marcas registradas sin permiso. Las plataformas revisan el anuncio Y el sitio al que mandas a la gente.</span>,
            <span><strong>Habla de ti, no de ellos:</strong> evita frases como "¿Estás cansado de tu jefe?" o "¿No tienes dinero?". Habla en positivo desde tu experiencia: "Me encanta este proyecto..." o "Buscamos personas que...".</span>,
          ]} />
        </Section>

        <Section value="multicontactos" icon={CheckCircle2} title="Cómo te ayuda MULTICONTACTOS" color="text-green-400" banner="ai-content">
          <Box tone="green" icon={Sparkles}>
            <p>Nuestra plataforma genera automáticamente <strong>mensajes, imágenes y videos</strong> con alta probabilidad de ser aprobados por las redes sociales.</p>
            <p>Aun así, <strong>supervisa siempre tu campaña antes de publicarla</strong> y verifica que no viole las políticas de cada red social. La responsabilidad final del contenido publicado es tuya.</p>
          </Box>
        </Section>

      </Accordion>
    </div>
  );
}
